import React, { useState, useEffect, useCallback, useRef } from 'react';
import TextField from '@atlaskit/textfield';

const DEFAULT_REPO_PAGE_SIZE = 15;

const Config = () => {
  const [repositories, setRepositories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [ap, setAp] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGitHubAuthenticated, setIsGitHubAuthenticated] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
  const [integrations, setIntegrations] = useState([]);
  const [updatingRepos, setUpdatingRepos] = useState(new Set());
  const [organizations, setOrganizations] = useState([]);
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const [showPurgeForm, setShowPurgeForm] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const [isPurging, setIsPurging] = useState(false);
  const [isGitLabAuthenticated, setIsGitLabAuthenticated] = useState(false);
  const [gitLabGroups, setGitLabGroups] = useState([]);
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);
  const [syncingIntegrationId, setSyncingIntegrationId] = useState(null);
  const [nextRepoCursor, setNextRepoCursor] = useState(null);
  const [hasMoreRepos, setHasMoreRepos] = useState(false);
  const [isLoadingMoreRepos, setIsLoadingMoreRepos] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [cursorHistory, setCursorHistory] = useState([null]); // start cursor for each page (page 0 => null)
  const [repoToDeleteId, setRepoToDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [popoverPosition, setPopoverPosition] = useState(null);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isLoadingRepos, setIsLoadingRepos] = useState(false); // Loading state for filter changes (doesn't hide whole page)
  const searchInputRef = useRef(null); // Ref to keep focus on search input
  const wasSearchFocusedRef = useRef(false); // Track if search was focused before loading

  // Debounce search query to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Re-fetch repositories when filters change
  useEffect(() => {
    if (ap) {
      // Reset pagination when filters change
      setNextRepoCursor(null);
      setCurrentPage(0);
      setCursorHistory([null]);
      setHasMoreRepos(false);
      
      const filters = {
        searchQuery: debouncedSearchQuery,
        statusFilter,
        providerFilter,
        projectFilter,
        ownerFilter,
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction
      };
      
      fetchRepositories({ cursorId: null, append: false, filters });
    }
  }, [ap, debouncedSearchQuery, statusFilter, providerFilter, projectFilter, ownerFilter, sortConfig]);

  const ownerOptions = React.useMemo(() => {
    const owners = new Set();
    repositories.forEach(repo => {
      if (repo.owner) {
        owners.add(repo.owner);
      }
    });
    return ['all', ...Array.from(owners).sort((a, b) => a.localeCompare(b))];
  }, [repositories]);

  useEffect(() => {
    const initializeAP = async () => {
      if (window.AP) {
        try {
          await new Promise((resolve) => window.AP.context.getContext(resolve));
          setAp(window.AP);
          setIsLoading(false);
        } catch (error) {
          console.error('Error initializing AP:', error);
          setError('Error initializing Atlassian Connect. Please refresh the page.');
          setIsLoading(false);
        }
      } else {
        console.error('AP is not available');
        setError('Atlassian Connect is not available. Please check your installation.');
        setIsLoading(false);
      }
    };

    initializeAP();
  }, []);

  useEffect(() => {
    if (ap) {
      fetchProjects();
      // Note: fetchRepositories is now called by the filter useEffect
      fetchRepositoryIntegrations();
      // Check URL parameters for org/group selector flags
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('github_auth_success') === 'true' && urlParams.get('show_org_selector') === 'true') {
        // Fetch and show organizations
        fetchOrganizations();
      }
      if (urlParams.get('gitlab_auth_success') === 'true' && urlParams.get('show_group_selector') === 'true') {
        fetchGitLabGroups();
      }
    }
  }, [ap]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (repoToDeleteId && !event.target.closest('[data-delete-popover]')) {
        setRepoToDeleteId(null);
        setDeleteConfirmText('');
        setPopoverPosition(null);
      }
    };

    if (repoToDeleteId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [repoToDeleteId]);

  // COMMENTED OUT: Automatic refresh/reprocessing of repositories
  // useEffect(() => {
  //   // Start polling for any repositories that are in progress
  //   const startPollingInProgressRepos = () => {
  //     repositories.forEach(repo => {
  //       if (repo.manifestGenerationStatus === 'in_progress' && !updatingRepos.has(repo.id)) {
  //         console.log('Starting polling for in-progress repo:', repo.id);
  //         handleUpdateIndex(repo.id);
  //       }
  //     });
  //   };

  //   // Check when repositories are loaded
  //   if (repositories.length > 0) {
  //     startPollingInProgressRepos();
  //   }
  // }, [repositories]); // Dependencies array includes repositories to catch initial load and updates

  // Polling for repository status updates (syncStatus, manifestGenerationStatus)
  // This automatically refreshes the UI when repositories change status (e.g., out-of-date)
  useEffect(() => {
    if (!ap) return;

    const pollRepositoryStatuses = async () => {
      // Don't poll if tab is not visible
      if (document.hidden) return;

      try {
        const token = await ap.context.getToken();
        const baseUrl = getBaseUrl(ap);

        const response = await fetch(`${baseUrl}/get-github-repos?pageSize=${DEFAULT_REPO_PAGE_SIZE}`, {
          method: 'GET',
          headers: {
            'Authorization': `JWT ${token}`,
            'Accept': 'application/json'
          },
          credentials: 'include'
        });

        if (!response.ok) return;

        const result = await response.json();
        const updatedRepos = result.repositories || [];

        // Update only status fields (preserve local state like isEditing)
        setRepositories(prev => prev.map(repo => {
          const fresh = updatedRepos.find(r => r.id === repo.id);
          if (fresh) {
            return {
              ...repo,
              syncStatus: fresh.syncStatus,
              manifestGenerationStatus: fresh.manifestGenerationStatus,
              manifestGenerationMessage: fresh.manifestGenerationMessage,
              processingTracker: fresh.processingTracker
            };
          }
          return repo;
        }));
      } catch (error) {
        console.error('Error polling repository statuses:', error);
      }
    };

    // Poll every 30 seconds
    const interval = setInterval(pollRepositoryStatuses, 30000);

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, [ap]);

  const fetchProjects = async () => {
    try {
      const response = await ap.request('/rest/api/3/project');
      const projectsData = JSON.parse(response.body);
      setProjects(projectsData.map(project => ({
        id: project.id,
        name: `${project.name} (${project.key})`
      })));
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Error loading projects');
    }
  };

  const getBaseUrl = (ap) => {
    try {
      // First try to get from ap.flagsUrl
      if (ap?.flagsUrl) {
        return new URL(ap.flagsUrl).origin;
      }
      
      // Fallback to getting from window location
      if (window?.location?.href) {
        const currentUrl = new URL(window.location.href);
        // If we're on Atlassian domain, use the iframeUrl
        if (currentUrl.hostname.includes('atlassian.net')) {
          return ap?.iframeUrl ? new URL(ap.iframeUrl).origin : (process.env.REACT_APP_BASE_URL || process.env.BASE_URL);
        }
        return currentUrl.origin;
      }
      
      // Final fallback to environment variable (REACT_APP_BASE_URL is available in React build)
      return process.env.REACT_APP_BASE_URL || process.env.BASE_URL;
    } catch (error) {
      console.error('Error getting base URL:', error);
      // Fallback to environment variable 
      return process.env.REACT_APP_BASE_URL || process.env.BASE_URL;
    }
  };

  const normalizeRepositories = useCallback((repoList = []) => (
    repoList.map(repo => ({
      ...repo,
      isEditing: false,
      selectedProjects: repo.projectIds || repo.selectedProjects || (repo.projectId ? [repo.projectId] : [])
    }))
  ), []);

  const renderProjectTag = (repoId, projectId, allowRemove) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return null;

    return (
      <span key={projectId} style={styles.projectTag}>
        {project.name}
        {allowRemove && (
          <button
            type="button"
            onClick={() => handleRemoveProject(repoId, projectId)}
            style={styles.removeButton}
            title="Remove project"
            onMouseEnter={(e) => e.target.style.color = '#DE350B'}
            onMouseLeave={(e) => e.target.style.color = '#006644'}
          >
            ×
          </button>
        )}
      </span>
    );
  };

  const renderSelectedProjects = (repo, { allowRemove }) => {
    const selectedProjects = repo.selectedProjects || [];

    if (selectedProjects.length === 0) {
      return allowRemove ? null : <span style={styles.notSet}>Not set</span>;
    }

    if (allowRemove) {
      return (
        <div style={styles.selectedProjects}>
          {selectedProjects.map(projectId => renderProjectTag(repo.id, projectId, true))}
        </div>
      );
    }

    // Display mode: show project name if one project, or dropdown with "Selected projects (X)" if multiple
    if (selectedProjects.length === 1) {
      const project = projects.find(p => p.id === selectedProjects[0]);
      return project ? (
        <span style={styles.selectedProjectsSelect}>{project.name}</span>
      ) : null;
    }

    return (
      <select 
        className="readonly-projects-select"
        value="" 
        onChange={(e) => {
          e.target.value = "";
        }}
        style={{
          ...styles.selectedProjectsSelect,
          cursor: 'default'
        }}
      >
        <option value="" disabled>
          Selected projects ({selectedProjects.length})
        </option>
        {selectedProjects.map(projectId => {
          const project = projects.find(p => p.id === projectId);
          return project ? (
            <option key={projectId} value={projectId}>{project.name}</option>
          ) : null;
        })}
      </select>
    );
  };

  const fetchRepositories = async ({ cursorId = null, append = false, filters = {} } = {}) => {
    try {
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);
      
      if (!baseUrl) {
        throw new Error('Could not determine base URL');
      }

      if (append) {
        setIsLoadingMoreRepos(true);
      } else {
        // Use isLoadingRepos for filter changes (doesn't hide whole page)
        // isLoading is only for initial page load
        setIsLoadingRepos(true);
        // Clear pagination state when doing a fresh fetch
        setNextRepoCursor(null);
        setHasMoreRepos(false);
      }

      const params = new URLSearchParams();
      params.append('pageSize', DEFAULT_REPO_PAGE_SIZE);
      if (cursorId) {
        params.append('cursorId', cursorId);
      }

      // Add filter parameters
      if (filters.searchQuery) {
        params.append('searchQuery', filters.searchQuery);
      }
      if (filters.statusFilter && filters.statusFilter !== 'all') {
        params.append('statusFilter', filters.statusFilter);
      }
      if (filters.providerFilter && filters.providerFilter !== 'all') {
        params.append('providerFilter', filters.providerFilter);
      }
      if (filters.projectFilter && filters.projectFilter !== 'all') {
        params.append('projectFilter', filters.projectFilter);
      }
      if (filters.ownerFilter && filters.ownerFilter !== 'all') {
        params.append('ownerFilter', filters.ownerFilter);
      }
      if (filters.sortKey) {
        params.append('sortKey', filters.sortKey);
      }
      if (filters.sortDirection) {
        params.append('sortDirection', filters.sortDirection);
      }

      const response = await fetch(`${baseUrl}/get-github-repos?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `JWT ${token}`,
          'Accept': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.error === 'LICENSE_INACTIVE') {
          // Don't set error state because backend might still return data
          ap.flag.create({
            type: 'warning',
            title: errorData.title || 'License Warning',
            body: `${errorData.message || 'License check failed'}. ${errorData.details || ''}`,
            actions: errorData.actions?.map(action => ({
              text: action.text,
              callback: () => {
                window.open(action.url, '_blank');
              }
            }))
          });
          // Continue trying to load data if available
        } else {
          throw new Error(errorData.message || errorData.error || `HTTP error - status: ${response.status}`);
        }
      }
      
      const result = await response.json().catch(() => ({}));
      const rawRepositories = Array.isArray(result?.repositories)
        ? result.repositories
        : Array.isArray(result)
          ? result
          : Array.isArray(result?.data?.repositories)
            ? result.data.repositories
            : [];

      if (rawRepositories.length === 0 && result && Object.keys(result).length > 0) {
        console.warn('Unexpected repository response shape, payload:', result);
      }

      const normalized = normalizeRepositories(rawRepositories);

      if (!append) {
        setRepositories(normalized);
      } else {
        setRepositories(prev => {
          const merged = [...prev];
          normalized.forEach(repo => {
            const existingIndex = merged.findIndex(existing => existing.id === repo.id);
            if (existingIndex >= 0) {
              merged[existingIndex] = { ...merged[existingIndex], ...repo };
            } else {
              merged.push(repo);
            }
          });
          return merged;
        });
      }

      const nextCursorId = result?.nextCursor?.id || result?.nextCursorId || result?.nextStartAfter || null;
      setNextRepoCursor(nextCursorId);
      setHasMoreRepos(Boolean(result?.hasMore));
    } catch (error) {
      console.error('Error fetching repositories:', error);
      setError(error.message || 'Error loading repositories');
      ap.flag.create({
        type: 'error',
        title: 'Error',
        body: error.message || 'Failed to load repositories. Please try again later.'
      });
      setIsGitHubAuthenticated(false);
      setHasMoreRepos(false);
      if (!append) {
        setNextRepoCursor(null);
      }
    } finally {
      if (append) {
        setIsLoadingMoreRepos(false);
      } else {
        setIsLoadingRepos(false);
        setIsLoading(false); // Also set isLoading false for initial load
      }
      
      // Restore search input focus if it was focused before loading
      if (wasSearchFocusedRef.current && searchInputRef.current) {
        // Use setTimeout to ensure the DOM has updated
        setTimeout(() => {
          searchInputRef.current?.focus();
          wasSearchFocusedRef.current = false;
        }, 0);
      }
    }
  };

  const handleLoadMoreRepos = async () => {
    if (!hasMoreRepos || isLoadingMoreRepos) {
      return;
    }

    const fallbackCursor = repositories[repositories.length - 1]?.id || null;
    const cursorToUse = nextRepoCursor || fallbackCursor;
    if (!cursorToUse) {
      console.warn('Cannot load more repositories because no cursor is available');
      return;
    }

    const filters = {
      searchQuery: debouncedSearchQuery,
      statusFilter,
      providerFilter,
      projectFilter,
      ownerFilter,
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction
    };

    await fetchRepositories({ cursorId: cursorToUse, append: true, filters });
  };

  const fetchRepositoryIntegrations = async () => {
    try {
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);
      
      if (!baseUrl) {
        throw new Error('Could not determine base URL');
      }

      const response = await fetch(`${baseUrl}/get-repository-integrations`, {
        method: 'GET',
        headers: {
          'Authorization': `JWT ${token}`,
          'Accept': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        // If 500 or other error, just return empty list instead of throwing
        console.warn('Error fetching repository integrations:', response.status);
        setIntegrations([]);
        return;
      }
      
      const result = await response.json();
      if (result && Array.isArray(result.integrations)) {
        setIntegrations(result.integrations);
      } else {
        // If unexpected format, just set empty list
        console.warn('Unexpected response format for repository integrations');
        setIntegrations([]);
      }
    } catch (error) {
      // Don't set error state - just log and show empty list
      console.error('Error fetching repository integrations:', error);
      setIntegrations([]);
    }
  };

  // Many-to-Many: Adding project to array
  const handleAddProject = (repoId, projectId) => {
    if (!projectId) return;
    setRepositories(repositories.map(repo => {
      if (repo.id === repoId) {
        const currentProjects = repo.selectedProjects || [];
        if (!currentProjects.includes(projectId)) {
          return { ...repo, selectedProjects: [...currentProjects, projectId] };
        }
      }
      return repo;
    }));
  };

  // Many-to-Many: Removing project from array
  const handleRemoveProject = async (repoId, projectId) => {
    try {
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);

      if (!baseUrl) {
        throw new Error('Could not determine base URL');
      }

      // Call backend endpoint to remove project
      const response = await fetch(`${baseUrl}/remove-repo-project-mapping`, {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          repoId: repoId,
          projectId: projectId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to remove project ${projectId} from repository`);
      }

      // Update local state after successful removal
      setRepositories(repositories.map(repo =>
        repo.id === repoId 
          ? { 
              ...repo, 
              selectedProjects: (repo.selectedProjects || []).filter(p => p !== projectId) 
            } 
          : repo
      ));

      ap.flag.create({
        type: 'success',
        title: 'Project Removed',
        body: `Project has been removed from repository successfully.`
      });
    } catch (error) {
      console.error('Error removing project:', error);
      ap.flag.create({
        type: 'error',
        title: 'Error Removing Project',
        body: error.message || 'Failed to remove project from repository.'
      });
    }
  };

  const handleSave = async (repo) => {
    try {      
        console.log('Saving repository-project mapping for repo:', repo);
        
        const token = await ap.context.getToken();
        const baseUrl = getBaseUrl(ap);

        if (!baseUrl) {
          throw new Error('Could not determine base URL');
        }

        // Many-to-Many: Send all selected projects
        const selectedProjects = repo.selectedProjects || [];
        if (selectedProjects.length === 0) {
          ap.flag.create({
            type: 'warning',
            title: 'No Projects Selected',
            body: 'Please select at least one project before saving.'
          });
          return;
        }

        // Send each project separately (backend will add to array)
        let lastError = null;
        for (const projectId of selectedProjects) {
          try {
            const response = await fetch(`${baseUrl}/save-repo-project-mapping`, {
                method: 'POST',
                headers: {
                    'Authorization': `JWT ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    selectedProject: projectId, 
                    createdAt: repo.createdAt, 
                    id: repo.id, 
                    integrationId: repo.integrationId, 
                    isEditing: repo.isEditing, 
                    isPrivate: repo.isPrivate, 
                    name: repo.name, 
                    owner: repo.owner, 
                    type: repo.type
                })
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              lastError = errorData.error || `Failed to save repository-project mapping for project ${projectId}`;
              console.error('Error saving project mapping:', lastError);
              // Continue with next project instead of throwing error
            }
          } catch (error) {
            lastError = error.message;
            console.error('Error saving project mapping:', error);
            // Continue with next project
          }
        }

        // If there were errors, show warning but don't throw error (because some projects may have been saved successfully)
        if (lastError) {
          ap.flag.create({
            type: 'warning',
            title: 'Partial Success',
            body: `Some projects were saved, but there was an error: ${lastError}. Please refresh the page to see current state.`
          });
        } else {
          console.log('Repository-project mapping saved successfully');
          ap.flag.create({
            type: 'success',
            title: 'Success',
            body: 'Projects have been successfully linked to repository.'
          });
        }
        
        // Close edit mode without refreshing - keep all loaded repositories visible
        setRepositories(prevRepos => prevRepos.map(r =>
          r.id === repo.id ? { ...r, isEditing: false } : r
        ));
        
        // Refresh only the indexing status for this repository
        await refreshRepositoryStatus(repo.id);
        
    } catch (error) {
      console.error('Error saving repository-project mapping:', error);
      ap.flag.create({
        type: 'error',
        title: 'Error',
        body: error.message || 'Failed to save repository-project mapping. Please try again.'
      });
    }
  };

  const handleEdit = (repoId) => {
    setRepositories(repositories.map(repo =>
      repo.id === repoId ? { ...repo, isEditing: true } : repo
    ));
  };

  const handleGitHubAuth = async () => {
    try {
      console.log('Starting GitHub authentication');
      
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);
      
      if (!baseUrl) {
        throw new Error('Could not determine base URL');
      }

      const response = await fetch(`${baseUrl}/start-github-auth`, {
        method: 'GET',
        headers: {
          'Authorization': `JWT ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.status !== 200) {
        throw new Error('Failed to start GitHub authentication');
      }

      const data = await response.json();

      console.log('GitHub authentication data:', data);

      if (data.authUrl) {
        window.open(data.authUrl, '_parent');
      } else {
        throw new Error('No auth URL received');
      }

      console.log('GitHub authentication started successfully');
    } catch (error) {
      console.error('Error starting GitHub auth:', error);
      setError('Failed to start GitHub authentication');
    }
  };

  const handleSort = (key, directionOverride) => {
    setSortConfig((prev) => {
      if (directionOverride) {
        return { key, direction: directionOverride };
      }

      let direction = 'ascending';
      if (prev.key === key && prev.direction === 'ascending') {
        direction = 'descending';
      }
      return { key, direction };
    });
  };

  const toggleSortDirection = () => {
    setSortConfig((prev) => ({
      ...prev,
      direction: prev.direction === 'ascending' ? 'descending' : 'ascending'
    }));
  };

  // Repositories are now filtered on the backend
  // We just return the repositories as-is (backend already sorted and filtered)
  const filteredRepositories = React.useMemo(() => {
    return repositories;
  }, [repositories]);

  const refreshRepositoryStatus = async (repoId) => {
    try {
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);
      
      const statusResponse = await fetch(`${baseUrl}/get-repository/${repoId}`, {
        headers: {
          'Authorization': `JWT ${token}`,
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!statusResponse.ok) {
        console.warn('Failed to refresh repository status:', statusResponse.status);
        return;
      }
      
      const repoData = await statusResponse.json();
      
      if (!repoData) {
        console.warn('No repository data received when refreshing status');
        return;
      }

      // Update only status-related fields, preserve everything else
      setRepositories(prev => prev.map(repo => 
        repo.id === repoId 
          ? {
              ...repo,
              manifestGenerationStatus: repoData.manifestGenerationStatus,
              manifestGenerationMessage: repoData.manifestGenerationMessage,
              processingTracker: repoData.processingTracker,
              syncStatus: repoData.syncStatus,
              lastStatusUpdate: repoData.lastStatusUpdate
            }
          : repo
      ));
    } catch (error) {
      console.warn('Error refreshing repository status:', error);
    }
  };

  const pollStatus = async (repoId, immediate = false) => {
    let pollCount = 0;
    const maxPolls = 300; // Maximum number of polling attempts (60 seconds)

    const doPoll = async () => {
      if (pollCount >= maxPolls) {
        setUpdatingRepos(prev => {
          const next = new Set(prev);
          next.delete(repoId);
          return next;
        });
        setRepositories(prev => prev.map(repo => 
          repo.id === repoId 
            ? { 
                ...repo, 
                manifestGenerationStatus: 'error',
                manifestGenerationMessage: 'Timeout waiting for update' 
              } 
            : repo
        ));
        return;
      }

      try {
        const token = await ap.context.getToken();
        const baseUrl = getBaseUrl(ap);
        
        const statusResponse = await fetch(`${baseUrl}/get-repository/${repoId}`, {
          headers: {
            'Authorization': `JWT ${token}`,
            'Accept': 'application/json'
          },
          credentials: 'include'
        });

        if (!statusResponse.ok) {
          const errorData = await statusResponse.json();
          if (statusResponse.status === 403 && errorData.error === 'LICENSE_INACTIVE') {
            setError(errorData.message);
            ap.flag.create({
              type: 'error',
              title: errorData.title,
              body: `${errorData.message} ${errorData.details}`,
              actions: errorData.actions?.map(action => ({
                text: action.text,
                callback: () => {
                  window.open(action.url, '_blank');
                }
              }))
            });
          } else {
            throw new Error(errorData.message || `HTTP error - status: ${statusResponse.status}`);
          }
          return;
        }
        
        const repoData = await statusResponse.json();
        
        if (!repoData) {
          throw new Error('No repository data received');
        }

        setRepositories(prev => prev.map(repo => 
          repo.id === repoId 
            ? {
                ...repo,  // Keep existing repo data
                ...repoData,  // Merge in ALL new data from the response 
                selectedProjects: repoData.projectIds || repo.selectedProjects || [], // Many-to-Many: Update project array
                manifestGenerationStatus: repoData.manifestGenerationStatus,
                manifestGenerationMessage: repoData.manifestGenerationMessage,
                processingTracker: repoData.processingTracker
              }
            : repo
        ));

        if (repoData.manifestGenerationStatus === 'in_progress') {
          pollCount++;
          // If immediate, check right away first, then continue with normal interval
          const delay = immediate && pollCount === 1 ? 2000 : 30000; // 2 seconds for first immediate poll, 30 seconds for others
          setTimeout(doPoll, delay);
        } else {
          setUpdatingRepos(prev => {
            const next = new Set(prev);
            next.delete(repoId);
            return next;
          });
        }
      } catch (error) {
        console.error('Error polling status:', error);
        setRepositories(prev => prev.map(repo => 
          repo.id === repoId 
            ? { 
                ...repo, 
                manifestGenerationStatus: 'error',
                manifestGenerationMessage: error.message || 'Failed to fetch repository status' 
              } 
            : repo
        ));
        setUpdatingRepos(prev => {
          const next = new Set(prev);
          next.delete(repoId);
          return next;
        });
      }
    };

    // If immediate, start right away, otherwise start with small delay
    if (immediate) {
      doPoll(); // Start polling immediately
    } else {
      setTimeout(doPoll, 1000); // Small delay for non-immediate polls
    }
  };

  const handleUpdateIndex = async (repoId) => {
    if (!repoId) {
      console.error('Attempted to update repository with undefined ID');
      return;
    }

    if (updatingRepos.has(repoId)) {
      console.log('Repository update already in progress:', repoId);
      return;
    }

    console.log('Updating repository:', { repoId });

    try {
      setRepositories(prev => prev.map(r => 
        r.id === repoId 
          ? { 
              ...r,
              manifestGenerationStatus: 'in_progress',
              manifestGenerationMessage: 'Starting manifest generation...' 
            } 
          : r
      ));
      
      setUpdatingRepos(prev => new Set([...prev, repoId]));
      
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);
      
      const response = await fetch(`${baseUrl}/update-repo-index/${repoId}`, {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${token}`,
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to update repository: ${response.statusText}`);
      }

      pollStatus(repoId);

    } catch (error) {
      console.error('Error updating repository:', { repoId, error });
      
      setRepositories(prev => prev.map(r => 
        r.id === repoId 
          ? { 
              ...r,
              manifestGenerationStatus: 'error',
              manifestGenerationMessage: error.message || 'Failed to start index update' 
            } 
          : r
      ));

      setUpdatingRepos(prev => {
        const next = new Set(prev);
        next.delete(repoId);
        return next;
      });
    }
  };

  const getProcessingPercentage = (repo) => {
    if (!repo.processingTracker) return null;
    
    const tracker = repo.processingTracker;
    const totalToProcess = (tracker.filesToProcess || 0) + (tracker.dirProcessQueued || 0);
    const totalProcessed = (tracker.filesProcessed || 0) + (tracker.dirProcessed || 0);
    
    // If nothing to process yet, return null 
    if (totalToProcess === 0) return null;
    
    // Cap at 100% to prevent display issues when processed > total
    const percentage = Math.round((totalProcessed / totalToProcess) * 100);
    return Math.min(percentage, 100);
  };

  const getStatusBadgeContent = (repo) => {
    if (repo.manifestGenerationStatus === 'in_progress') {
      const percentage = getProcessingPercentage(repo);
      return percentage !== null ? `Processing ${percentage}%` : 'Calculating...';
    }
    return repo.manifestGenerationStatus || 'unknown';
  };

  const getStatusBadgeStyle = (status) => {
    const baseStyle = {
      padding: '6px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      display: 'inline-block',
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    };

    switch (status) {
      case 'completed':
        return { ...baseStyle, backgroundColor: '#00875A', color: 'white' };
      case 'error':
        return { ...baseStyle, backgroundColor: '#DE350B', color: 'white' };
      case 'in_progress':
        return { ...baseStyle, backgroundColor: '#0052CC', color: 'white' };
      case 'pending':
        return { ...baseStyle, backgroundColor: '#6B778C', color: 'white' };
      case 'out-of-date':
        return { ...baseStyle, backgroundColor: '#FF991F', color: 'white' };
      default:
        return { ...baseStyle, backgroundColor: '#8993A4', color: 'white' };
    }
  };

  const StatusIndicator = ({ isUpdating, repoId }) => {
    if (!isUpdating) {
      return (
        <div 
          onClick={() => handleUpdateIndex(repoId)}
          style={{ 
            padding: '4px',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
          </svg>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ marginRight: '8px', fontSize: '12px', color: '#6B778C' }}>
          Analyzing
        </span>
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="currentColor"
          style={{
            animation: 'spin 2s linear infinite'
          }}
        >
          <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm8 12c0 4.418-3.582 8-8 8s-8-3.582-8-8 3.582-8 8-8 8 3.582 8 8zm-19 0c0-6.065 4.935-11 11-11v2c-4.962 0-9 4.038-9 9 0 2.481 1.009 4.731 2.639 6.361l-1.414 1.414c-2.036-2.036-3.225-4.686-3.225-7.775z"/>
        </svg>
      </div>
    );
  };

  const formatLastChange = (dateValue) => {

    const noData = '-'

      if (!dateValue) return noData;
      
      // Handle Firestore timestamp format
      if (dateValue._seconds) {
        return new Date(dateValue._seconds * 1000).toLocaleString();
      }
      
      // Handle ISO string or Date object
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return noData;
      
      return date.toLocaleString();
    };

  const renderRepositoryFilters = () => (
    <div style={styles.filterWrapper}>
      <div style={styles.filterHeaderRow}>
        <button
          type="button"
          style={styles.filterToggleButton}
          onClick={() => setIsFilterPanelOpen(prev => !prev)}
        >
          {isFilterPanelOpen ? 'Hide search & filters' : 'Show search & filters'}
        </button>
        <div style={styles.sortControls}>
          <span style={styles.sortLabel}>Sort by</span>
          <select
            style={styles.sortSelect}
            value={sortConfig.key || 'name'}
            onChange={(e) => handleSort(e.target.value, 'ascending')}
          >
            <option value="name">Name</option>
            <option value="owner">Owner</option>
            <option value="url">URL</option>
            <option value="manifestGenerationStatus">Indexing Status</option>
          </select>
          <button
            type="button"
            style={styles.sortDirectionButton}
            onClick={toggleSortDirection}
          >
            {sortConfig.direction === 'ascending' ? 'Asc (A-Z)' : 'Desc (Z-A)'}
          </button>
        </div>
      </div>
      {isFilterPanelOpen && (
        <div style={styles.filterPanel}>
          <div style={styles.filterRow}>
            <div style={styles.filterGroupFullWidth}>
              <div style={styles.filterGroupTitle}>Search repositories</div>
              <input
                ref={searchInputRef}
                id="repo-search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  wasSearchFocusedRef.current = true; // Mark as focused when typing
                  setSearchQuery(e.target.value);
                }}
                placeholder="Search by name, URL, or owner"
                style={styles.searchInput}
                onFocus={(e) => {
                  wasSearchFocusedRef.current = true;
                  e.target.style.borderColor = '#0052CC';
                  e.target.style.boxShadow = '0 0 0 2px rgba(0, 82, 204, 0.2)';
                }}
                onBlur={(e) => {
                  // Only reset if not loading (loading causes blur, we want to restore focus after)
                  if (!isLoadingRepos) {
                    wasSearchFocusedRef.current = false;
                  }
                  e.target.style.borderColor = '#DFE1E6';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>
          <div style={styles.filterRow}>
            <div style={styles.filterGroup}>
              <div style={styles.filterGroupTitle}>Source</div>
              <div style={styles.filterChips}>
                {[
                  { label: 'All', value: 'all' },
                  { label: 'GitHub', value: 'github' },
                  { label: 'GitLab', value: 'gitlab' },
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setProviderFilter(option.value)}
                    style={{
                      ...styles.filterChip,
                      ...(providerFilter === option.value ? styles.filterChipActive : {})
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={styles.filterGroup}>
              <div style={styles.filterGroupTitle}>Indexing status</div>
              <div style={styles.filterChips}>
                {[
                  { label: 'All', value: 'all' },
                  { label: 'Completed', value: 'completed' },
                  { label: 'Pending / In Progress', value: 'pending' },
                  { label: 'Failed', value: 'failed' },
                  { label: 'Out of date', value: 'out-of-date' },
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatusFilter(option.value)}
                    style={{
                      ...styles.filterChip,
                      ...(statusFilter === option.value ? styles.filterChipActive : {})
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div style={styles.filterRow}>
            <div style={styles.filterGroup}>
              <div style={styles.filterGroupTitle}>Projects</div>
              <div style={styles.filterChips}>
                {[
                  { label: 'All', value: 'all' },
                  { label: 'Has Project', value: 'has' },
                  { label: 'No Project', value: 'none' },
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setProjectFilter(option.value)}
                    style={{
                      ...styles.filterChip,
                      ...(projectFilter === option.value ? styles.filterChipActive : {})
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={styles.filterGroup}>
              <div style={styles.filterGroupTitle}>Owner</div>
              <select
                style={styles.filterSelect}
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
              >
                <option value="all">All owners</option>
                {ownerOptions
                  .filter(option => option !== 'all')
                  .map(owner => (
                    <option key={owner} value={owner}>{owner}</option>
                  ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderRepositoriesTable = () => (
    <div style={{ position: 'relative' }}>
      {/* Loading overlay for filter changes */}
      {isLoadingRepos && (
        <div style={styles.tableLoadingOverlay}>
          <div style={styles.loadingSpinner}>
            <svg 
              width="32" 
              height="32" 
              viewBox="0 0 24 24" 
              fill="#0052CC"
              style={{ animation: 'spin 1s linear infinite' }}
            >
              <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm8 12c0 4.418-3.582 8-8 8s-8-3.582-8-8 3.582-8 8-8 8 3.582 8 8zm-19 0c0-6.065 4.935-11 11-11v2c-4.962 0-9 4.038-9 9 0 2.481 1.009 4.731 2.639 6.361l-1.414 1.414c-2.036-2.036-3.225-4.686-3.225-7.775z"/>
            </svg>
            <span style={{ marginTop: '8px', color: '#0052CC', fontWeight: 500 }}>Loading repositories...</span>
          </div>
        </div>
      )}
      <table style={{...styles.table, opacity: isLoadingRepos ? 0.5 : 1, transition: 'opacity 0.2s ease'}}>
        <thead>
          <tr>
            <th style={{ ...styles.th, cursor: 'default', width: '60px' }}>#</th>
            <th 
              style={{ ...styles.th, cursor: 'default' }} 
            >
              Owner
            </th>
            <th 
              style={{ ...styles.th, cursor: 'default' }} 
            >
              Name
            </th>
            <th style={{...styles.th, ...styles.projectsHeader, cursor: 'default'}}>Projects</th>
            <th style={{...styles.th, cursor: 'default'}}>Actions</th>
            <th 
              style={{ ...styles.th, cursor: 'default' }} 
            >
              Indexing Status
            </th>
            <th style={{...styles.th, cursor: 'default', width: '50px'}}></th>
          </tr>
        </thead>
        <tbody>
        {filteredRepositories.map((repo, index) => (
          <tr 
            key={repo.id}
            style={{
              backgroundColor: index % 2 === 0 ? 'white' : '#FAFBFC',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F4F5F7';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#FAFBFC';
            }}
          >
            <td style={styles.indexCell}>
              {index + 1}
            </td>
            <td style={{
              ...styles.td, 
              fontWeight: '500', 
              color: '#42526E',
              fontSize: '13px'
            }}>
              {repo.owner}
            </td>
            <td style={{
              ...styles.td, 
              fontWeight: '600', 
              color: '#172B4D',
              fontSize: '14px'
            }}>
              <a 
                href={repo.url} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#0052CC',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  maxWidth: '240px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={repo.url}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                  e.currentTarget.style.color = '#0065FF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                  e.currentTarget.style.color = '#0052CC';
                }}
              >
                {repo.name}
                <span style={{ fontSize: '10px', opacity: 0.7 }}>↗</span>
              </a>
            </td>
            <td style={styles.td}>
              {repo.isEditing ? (
                <div style={styles.projectSelector}>
                  {renderSelectedProjects(repo, { allowRemove: true })}
                  {/* Dropdown for adding new project */}
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddProject(repo.id, e.target.value);
                        e.target.value = ''; // Reset dropdown
                      }
                    }}
                    style={styles.select}
                  >
                    <option value="">Add Project...</option>
                    {projects
                      .filter(p => !(repo.selectedProjects || []).includes(p.id))
                      .map(project => (
                        <option key={project.id} value={project.id}>{project.name}</option>
                      ))}
                  </select>
                </div>
              ) : (
                <div>
                  {renderSelectedProjects(repo, { allowRemove: false })}
                  {repo.updatedAt && (
                    <div style={styles.lastChangedText}>
                      Last changed: {formatLastChange(repo.updatedAt)}
                    </div>
                  )}
                </div>
              )}
            </td>
            <td style={styles.td}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {repo.isEditing ? (
                  <button 
                    onClick={() => handleSave(repo)} 
                    style={styles.button}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#0065FF';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#0052CC';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)';
                    }}
                  >
                    Save
                  </button>
                ) : (
                  <button 
                    onClick={() => handleEdit(repo.id)} 
                    style={styles.button}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#0065FF';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#0052CC';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)';
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
            </td>
            <td style={styles.td}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <div
                    style={getStatusBadgeStyle(repo.manifestGenerationStatus)}
                    title={repo.manifestGenerationMessage || ''}
                  >
                    {getStatusBadgeContent(repo)}
                  </div>
                  {(repo.selectedProjects && repo.selectedProjects.length > 0) && (
                    <div 
                      style={{ 
                        color: updatingRepos.has(repo.id) ? '#0065FF' : '#0052CC',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease',
                      }}
                      title="Update Index"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <StatusIndicator 
                        isUpdating={updatingRepos.has(repo.id)}
                        repoId={repo.id}
                      />
                    </div>
                  )}
                </div>
                {repo.lastStatusUpdate && (
                  <div style={styles.lastChangedText}>
                    Last changed: {formatLastChange(repo.lastStatusUpdate)}
                  </div>
                )}
              </div>
            </td>
            <td style={{...styles.td, padding: '8px', verticalAlign: 'top', width: '50px', position: 'relative'}}>
              <div style={{ position: 'relative', display: 'inline-block' }} data-delete-popover>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(repo, e);
                  }}
                  style={styles.deleteButtonInRow}
                  title="Delete repository"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#FFEBE6';
                    e.currentTarget.style.color = '#DE350B';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#6B778C';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                </button>
                {repoToDeleteId === repo.id && popoverPosition && (
                  <div style={{
                    ...styles.deleteConfirmationPopover,
                    top: `${popoverPosition.top}px`,
                    right: `${popoverPosition.right}px`,
                    bottom: popoverPosition.bottom
                  }}>
                    <div style={styles.deleteConfirmationContent}>
                      <p style={styles.deleteConfirmationText}>
                        Are you sure you want to delete this repository?
                      </p>
                      <p style={styles.deleteConfirmationRepoName}>
                        {repo.owner}/{repo.name}
                      </p>
                      <p style={styles.deleteConfirmationInstruction}>
                        Type <strong>DELETE</strong> to confirm:
                      </p>
                      <div style={{ marginBottom: '16px' }}>
                        <TextField
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder="Type DELETE to confirm"
                          isRequired
                        />
                      </div>
                      <div style={styles.deleteConfirmationButtons}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCancel();
                          }}
                          style={styles.deleteCancelButton}
                          disabled={isDeleting}
                          onMouseEnter={(e) => {
                            if (!isDeleting) {
                              e.target.style.backgroundColor = '#F4F5F7';
                              e.target.style.borderColor = '#C1C7D0';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isDeleting) {
                              e.target.style.backgroundColor = 'white';
                              e.target.style.borderColor = '#DFE1E6';
                            }
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteConfirm(repo);
                          }}
                          style={{
                            ...styles.deleteConfirmButton,
                            ...(isDeleting || deleteConfirmText !== 'DELETE' ? styles.deleteConfirmButtonDisabled : {})
                          }}
                          disabled={isDeleting || deleteConfirmText !== 'DELETE'}
                          onMouseEnter={(e) => {
                            if (!isDeleting && deleteConfirmText === 'DELETE') {
                              e.target.style.backgroundColor = '#BF2600';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isDeleting && deleteConfirmText === 'DELETE') {
                              e.target.style.backgroundColor = '#DE350B';
                            }
                          }}
                        >
                          {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );

  const handleSyncRepositories = async (integrationId) => {
    try {
      setSyncingIntegrationId(integrationId);
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);
      
      if (!baseUrl) {
        throw new Error('Could not determine base URL');
      }

      const response = await fetch(`${baseUrl}/sync-repositories/${integrationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${token}`,
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Repository sync result:', result);
      
      // Refresh the repositories list after syncing with current filters
      const filters = {
        searchQuery: debouncedSearchQuery,
        statusFilter,
        providerFilter,
        projectFilter,
        ownerFilter,
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction
      };
      await fetchRepositories({ cursorId: null, append: false, filters });
      const repoCount = result.repositories?.length ?? 0;

      if (repoCount === 0) {
        window.AP.flag.create({
          type: 'warning',
          title: 'Sync Completed (No Repositories)',
          body: 'Sync completed, but no repositories were found for this integration.'
        });
      } else {
        window.AP.flag.create({
          type: 'success',
          title: 'Repositories Synchronized',
          body: `Successfully synced ${repoCount} repositories.`
        });
      }

      window.AP.flag.create({
        type: 'success',
        title: 'Repositories synchronized',
        body: 'Sync completed.'
      });
    } catch (error) {
      console.error('Error syncing repositories:', error);
      window.AP.flag.create({
        type: 'error',
        title: 'Sync Failed',
        body: 'Failed to synchronize repositories. Please try again.'
      });
    } finally {
      setSyncingIntegrationId(null);
    }
  };

  const handleDeleteClick = (repo, event) => {
    setRepoToDeleteId(repo.id);
    setDeleteConfirmText(''); // Reset confirmation text when opening popover
    
    // Calculate popover position
    if (event && event.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      const popoverHeight = 200; // Approximate height of popover
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      
      // Position above if there's enough space, otherwise below
      if (spaceAbove >= popoverHeight + 10 || spaceAbove > spaceBelow) {
        setPopoverPosition({
          top: rect.top - popoverHeight - 8,
          right: window.innerWidth - rect.right,
          bottom: 'auto'
        });
      } else {
        setPopoverPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right,
          bottom: 'auto'
        });
      }
    }
  };

  const handleDeleteCancel = () => {
    setRepoToDeleteId(null);
    setDeleteConfirmText('');
    setPopoverPosition(null);
  };

  const handleDeleteConfirm = async (repo) => {
    if (!repo || !ap) return;

    try {
      setIsDeleting(true);
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);
      
      if (!baseUrl) {
        throw new Error('Could not determine base URL');
      }

      const response = await fetch(`${baseUrl}/delete-repository/${repo.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `JWT ${token}`,
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      // Refresh the repositories list after deletion with current filters
      const filters = {
        searchQuery: debouncedSearchQuery,
        statusFilter,
        providerFilter,
        projectFilter,
        ownerFilter,
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction
      };
      await fetchRepositories({ cursorId: null, append: false, filters });

      // Scroll to top after deletion
      window.scrollTo({ top: 0, behavior: 'smooth' });

      ap.flag.create({
        type: 'success',
        title: 'Repository Deleted',
        body: `Repository ${repo.owner}/${repo.name} has been deleted successfully.`
      });

      setRepoToDeleteId(null);
      setDeleteConfirmText('');
      setPopoverPosition(null);
    } catch (error) {
      console.error('Error deleting repository:', error);
      ap.flag.create({
        type: 'error',
        title: 'Delete Failed',
        body: error.message || 'Failed to delete repository. Please try again.'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const renderIntegrationsTable = () => (
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>System</th>
          <th style={styles.th}>Authorizing User</th>
          <th style={styles.th}>Organizations Approved</th>
          <th style={styles.th}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {integrations.length === 0 ? (
          <tr>
            <td colSpan="4" style={{ ...styles.td, textAlign: 'center', color: '#6B778C', padding: '20px' }}>
              No repository integrations found. Authenticate with GitHub or GitLab to get started.
            </td>
          </tr>
        ) : (
          integrations.map(integration => (
            <tr key={integration.id}>
              <td style={styles.td}>{integration.system}</td>
              <td style={styles.td}>{integration.userData?.name || integration.userData?.username || 'N/A'}</td>
              <td style={styles.td}>
                {integration.organizations?.map(org => org.fullPath).join(', ') || 'None'}
              </td>
              <td style={styles.td}>
                <button 
                  onClick={() => handleSyncRepositories(integration.id)} 
                  style={styles.button}
                  disabled={syncingIntegrationId !== null}
                >
                  {syncingIntegrationId === integration.id ? 'Syncing...' : 'Sync Repositories'}
                </button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  const handleOrgSelection = async (orgId) => {
    try {
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);
      const response = await fetch(`${baseUrl}/select-github-org`, {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orgId })
      });

      if (!response.ok) {
        throw new Error('Failed to select organization');
      }

      // Refresh the integrations list
      await fetchRepositoryIntegrations();
      setShowOrgSelector(false);
    } catch (error) {
      console.error('Error selecting organization:', error);
      setError('Failed to select organization');
    }
  };

  const renderOrgSelector = () => (
    showOrgSelector && (
      <div style={styles.orgSelector}>
        <h3>Select GitHub Organization</h3>
        <div style={styles.orgList}>
          {organizations.map(org => (
            <button
              key={org.id}
              onClick={() => handleOrgSelection(org.id)}
              style={styles.orgButton}
            >
              {org.login}
            </button>
          ))}
        </div>
      </div>
    )
  );

  const fetchOrganizations = async () => {
    try {
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);
      const response = await fetch(`${baseUrl}/get-github-orgs`, {
        method: 'GET',
        headers: {
          'Authorization': `JWT ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const data = await response.json();
      setOrganizations(data.organizations);
      setShowOrgSelector(true);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setError('Failed to fetch organizations');
    }
  };

  const handlePurgeData = async () => {
    if (purgeConfirmText !== 'DELETE') {
      ap.flag.error({ title: 'Error', body: 'Please type DELETE to confirm' });
      return;
    }

    try {
      setIsPurging(true);
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);
      
      const response = await fetch(`${baseUrl}/purge-account-data`, {
        method: 'DELETE',
        headers: {
          'Authorization': `JWT ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirmText: purgeConfirmText })
      });

      if (!response.ok) {
        throw new Error('Failed to purge data');
      }

      window.AP.flag.create({
        type: 'success',
        title: 'Success',
        body: 'All data has been purged from CodeMerlin'
      });
      
      setShowPurgeForm(false);
      setPurgeConfirmText('');
      
      setTimeout(() => {
        console.log('Reloading page...');
        window.scrollTo(0, 0);  // Scroll to top first
        setTimeout(() => {      // Small delay to ensure scroll completes
          window.location.href = window.location.href;
        }, 100);
      }, 1500);
    } catch (error) {
      console.error('Error purging data:', error);
      ap.flag.error({ title: 'Error', body: 'Failed to purge data. Please try again.' });
    } finally {
      setIsPurging(false);
    }
  };

  const handleGitLabAuth = async () => {
    try {
      console.log('Starting GitLab authentication');
      
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);
      
      if (!baseUrl) {
        throw new Error('Could not determine base URL');
      }

      const response = await fetch(`${baseUrl}/start-gitlab-auth`, {
        method: 'GET',
        headers: {
          'Authorization': `JWT ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.status !== 200) {
        throw new Error('Failed to start GitLab authentication');
      }

      const data = await response.json();
      console.log('GitLab authentication data:', data);

      if (data.authUrl) {
        window.open(data.authUrl, '_parent');
      } else {
        throw new Error('No auth URL received');
      }

      console.log('GitLab authentication started successfully');
    } catch (error) {
      console.error('Error starting GitLab auth:', error);
      setError('Failed to start GitLab authentication');
    }
  };

  const fetchGitLabGroups = async () => {
    try {
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);
      const response = await fetch(`${baseUrl}/get-gitlab-groups`, {
        method: 'GET',
        headers: {
          'Authorization': `JWT ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch groups');
      }

      const data = await response.json();
      setGitLabGroups(data.groups);
      setShowGroupSelector(true);
    } catch (error) {
      console.error('Error fetching GitLab groups:', error);
      setError('Failed to fetch GitLab groups');
    }
  };

  const handleGroupSelection = async (groupId) => {
    try {
      const token = await ap.context.getToken();
      const baseUrl = getBaseUrl(ap);
      const response = await fetch(`${baseUrl}/select-gitlab-group`, {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ groupId })
      });

      if (!response.ok) {
        throw new Error('Failed to select group');
      }

      // Refresh the integrations list
      await fetchRepositoryIntegrations();
      setShowGroupSelector(false);
    } catch (error) {
      console.error('Error selecting GitLab group:', error);
      setError('Failed to select group');
    }
  };

  const renderGroupSelector = () => (
    showGroupSelector && (
      <div style={styles.orgSelector}>
        <h3>Select GitLab Group</h3>
        <div style={styles.orgList}>
          {gitLabGroups.map(group => (
            <button
              key={group.id}
              onClick={() => handleGroupSelection(group.id)}
              style={styles.orgButton}
            >
              {group.name}
            </button>
          ))}
        </div>
      </div>
    )
  );

  if (isLoading) return <div>Loading...</div>;
  if (error) return (
    <div style={styles.errorContainer}>
      <div style={styles.errorCard}>
        <div style={styles.errorIcon}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="#DE350B">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
        </div>
        <h2 style={styles.errorTitle}>License Not Active</h2>
        <p style={styles.errorMessage}>{error}</p>
        <button 
          onClick={() => window.open('https://marketplace.atlassian.com/apps/1227073/codemerlin', '_blank')}
          style={styles.marketplaceButton}
        >
          Visit Marketplace
        </button>
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>Amulent CodeMerlin Configuration</h1>
      <div style={styles.authSection}>
        <div style={styles.githubAuth}>
          {!isGitHubAuthenticated ? (
            <button onClick={handleGitHubAuth} style={styles.button}>
              Authenticate with GitHub
            </button>
          ) : (
            <p>GitHub authenticated</p>
          )}
        </div>
        <div style={styles.gitlabAuth}>
          {!isGitLabAuthenticated ? (
            <button onClick={handleGitLabAuth} style={styles.button}>
              Authenticate with GitLab
            </button>
          ) : (
            <p>GitLab authenticated</p>
          )}
        </div>
      </div>
      <h2 style={styles.subheading}>Repository Integrations</h2>
      {renderIntegrationsTable()}
      <h2 style={styles.subheading}>Code Repositories</h2>
      {renderRepositoryFilters()}
      {renderRepositoriesTable()}
      {hasMoreRepos && (
        <div style={styles.paginationControls}>
          <button
            onClick={handleLoadMoreRepos}
            style={styles.button}
            disabled={isLoadingMoreRepos}
          >
            {isLoadingMoreRepos ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
      {renderOrgSelector()}
      {renderGroupSelector()}
      <div style={styles.dangerZone}>
        <h2 style={styles.dangerTitle}>Danger Zone</h2>
        <button 
          onClick={() => setShowPurgeForm(!showPurgeForm)} 
          style={styles.dangerButton}
        >
          Delete All Data In CodeMerlin
        </button>
        {showPurgeForm && (
          <div style={styles.purgeForm}>
            <p style={styles.warningText}>
              This action will permanently delete all Customer Data from CodeMerlin, including:
            </p>
            <ul style={styles.warningList}>
              <li>All code repository indexes</li>
              <li>All integrations</li>
              <li>All issue data</li>
              <li>All analysis history</li>
            </ul>
            <p style={styles.confirmText}>
              This action cannot be undone. Please type DELETE to confirm:
            </p>
            <TextField
              value={purgeConfirmText}
              onChange={(e) => setPurgeConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              isRequired
            />
            <button 
              onClick={handlePurgeData}
              style={styles.dangerButton}
              disabled={purgeConfirmText !== 'DELETE' || isPurging}
            >
              {isPurging ? 'Purging...' : 'Delete All Data'}
            </button>
          </div>
        )}
      </div>
      
      <div style={styles.footer}>
        <h3 style={styles.footerTitle}>Important Links</h3>
        <div style={styles.footerLinks}>
          <a href="https://www.amulent.com/data-processing-addendum" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>
            Data Processing Addendum
          </a>
          <a href="https://www.amulent.com/privacy" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>
            Privacy Policy
          </a>
          <a href="https://www.amulent.com/security" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>
            Security Statement
          </a>
          <a href="https://www.amulent.com/subprocessors" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>
            Subprocessors
          </a>
          <a href="https://www.amulent.com/terms" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>
            Terms of Service
          </a>
          <a href="https://amulent.atlassian.net/servicedesk/customer/portal/1" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>
            Support
          </a>
          <a href="https://amulent.atlassian.net/wiki/spaces/CFJD/overview" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>
            User Documentation
          </a>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '30px',
    backgroundColor: 'white',
    borderRadius: '0',
    maxWidth: '1200px',
    margin: '0 auto',
    overflow: 'visible', // Prevent double scroll
  },
  heading: {
    color: '#0052CC',
    fontSize: '24px',
    marginBottom: '20px',
  },
  subheading: {
    marginTop: '30px',
    fontSize: '20px',
    color: '#172B4D',
  },
  authSection: {
    display: 'flex',
    gap: '20px',
    marginBottom: '20px',
  },
  githubAuth: {
    marginTop: '20px',
    marginBottom: '30px',
  },
  gitlabAuth: {
    marginTop: '20px',
    marginBottom: '30px',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0',
    marginTop: '20px',
    backgroundColor: 'white',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
  },
  filterWrapper: {
    marginTop: '12px',
    marginBottom: '8px',
    backgroundColor: '#F4F5F7',
    border: '1px solid #DFE1E6',
    borderRadius: '8px',
    padding: '12px',
  },
  filterHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  filterToggleButton: {
    backgroundColor: 'white',
    color: '#172B4D',
    border: '1px solid #DFE1E6',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontWeight: 600,
    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
  },
  sortControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  sortLabel: {
    fontSize: '12px',
    color: '#6B778C',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: 600,
  },
  sortSelect: {
    padding: '8px 10px',
    border: '1px solid #DFE1E6',
    borderRadius: '4px',
    fontSize: '13px',
    minWidth: '160px',
    backgroundColor: 'white',
  },
  sortDirectionButton: {
    padding: '8px 10px',
    border: '1px solid #DFE1E6',
    borderRadius: '4px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    color: '#172B4D',
  },
  filterPanel: {
    marginTop: '12px',
    backgroundColor: 'white',
    border: '1px solid #DFE1E6',
    borderRadius: '8px',
    padding: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '12px',
  },
  filterGroup: {
    flex: '1 1 260px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  filterGroupFullWidth: {
    flex: '1 1 100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  filterGroupTitle: {
    fontSize: '12px',
    color: '#6B778C',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: 600,
  },
  filterChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  filterChip: {
    border: '1px solid #DFE1E6',
    backgroundColor: 'white',
    color: '#172B4D',
    padding: '6px 10px',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },
  filterChipActive: {
    backgroundColor: '#0052CC',
    color: 'white',
    borderColor: '#0052CC',
  },
  filterSelect: {
    padding: '8px 10px',
    border: '1px solid #DFE1E6',
    borderRadius: '4px',
    fontSize: '13px',
    backgroundColor: 'white',
  },
  th: {
    textAlign: 'left',
    padding: '14px 16px',
    borderBottom: '2px solid #E4E7EB',
    backgroundColor: '#FAFBFC',
    color: '#172B4D',
    fontWeight: '600',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    cursor: 'default',
    transition: 'all 0.2s ease',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    userSelect: 'none',
  },
  td: {
    padding: '16px',
    borderBottom: '1px solid #F4F5F7',
    verticalAlign: 'top',
    transition: 'background-color 0.15s ease',
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #DFE1E6',
    borderRadius: '4px',
    fontSize: '13px',
    backgroundColor: 'white',
    transition: 'border-color 0.2s ease',
  },
  button: {
    padding: '8px 16px',
    backgroundColor: '#0052CC',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    marginRight: '10px',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  keyframes: `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `,
  orgSelector: {
    padding: '20px',
    backgroundColor: '#F4F5F7',
    borderRadius: '4px',
    marginTop: '20px',
  },
  orgList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  orgButton: {
    padding: '8px 16px',
    backgroundColor: 'white',
    border: '1px solid #DFE1E6',
    borderRadius: '3px',
    cursor: 'pointer',
    textAlign: 'left',
  },
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '80vh',
    padding: '20px',
    backgroundColor: '#F4F5F7',
  },
  errorCard: {
    backgroundColor: 'white',
    borderRadius: '3px',
    padding: '40px',
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
    maxWidth: '500px',
    width: '100%',
  },
  errorIcon: {
    marginBottom: '16px',
  },
  errorTitle: {
    color: '#172B4D',
    fontSize: '24px',
    marginBottom: '16px',
    fontWeight: '500',
  },
  errorMessage: {
    color: '#42526E',
    fontSize: '16px',
    lineHeight: '1.5',
    marginBottom: '24px',
  },
  marketplaceButton: {
    backgroundColor: '#0052CC',
    color: 'white',
    padding: '8px 24px',
    borderRadius: '3px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#0065FF',
    },
  },
  dangerZone: {
    marginTop: '40px',
    padding: '24px',
    border: '1px solid #BF2600',
    borderRadius: '3px'
  },
  dangerTitle: {
    color: '#BF2600',
    fontSize: '16px',
    marginBottom: '16px'
  },
  dangerButton: {
    backgroundColor: '#BF2600',
    color: 'white',
    padding: '8px 16px',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#DE350B'
    }
  },
  modalContent: {
    padding: '24px 0',
    fontSize: '14px',
    lineHeight: '1.5',
  },
  warningText: {
    marginBottom: '16px',
    color: '#172B4D',
    fontWeight: 500
  },
  warningList: {
    marginBottom: '24px',
    paddingLeft: '24px',
    color: '#172B4D'
  },
  confirmText: {
    marginBottom: '16px',
    color: '#172B4D'
  },
  purgeForm: {
    marginTop: '20px',
    padding: '20px',
    backgroundColor: '#F4F5F7',
    borderRadius: '4px',
  },
  footer: {
    marginTop: '40px',
    paddingTop: '24px',
    borderTop: '1px solid #DFE1E6',
  },
  footerTitle: {
    fontSize: '16px',
    color: '#172B4D',
    marginBottom: '16px',
  },
  footerLinks: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
  },
  footerLink: {
    color: '#0052CC',
    textDecoration: 'none',
    fontSize: '14px',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  notSet: {
    backgroundColor: '#FFEBE6',
    color: '#DE350B',
    padding: '5px 10px',
    borderRadius: '4px',
    fontWeight: '500',
    fontSize: '12px',
    display: 'inline-block',
    border: '1px solid #FFC7C2'
  },
  projectSelector: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  indexCell: {
    width: '60px',
    textAlign: 'center',
    padding: '16px',
    borderBottom: '1px solid #F4F5F7',
    fontWeight: '600',
    color: '#6B778C',
    verticalAlign: 'top'
  },
  paginationControls: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'center'
  },
  selectedProjects: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '6px'
  },
  selectedProjectsSelect: {
    width: '100%',
    minWidth: '220px',
    padding: '8px 12px',
    border: '1px solid #DFE1E6',
    borderRadius: '6px',
    fontSize: '12px',
    backgroundColor: '#FAFBFC',
    color: '#172B4D',
    boxSizing: 'border-box'
  },
  projectsHeader: {
    minWidth: '240px'
  },
  projectsCell: {
    minWidth: '240px',
    overflow: 'visible'
  },
  projectTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#E3FCEF',
    color: '#006644',
    padding: '5px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    border: '1px solid #B3D9C7',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  removeButton: {
    background: 'none',
    border: 'none',
    color: '#006644',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    padding: '0',
    marginLeft: '4px',
    lineHeight: '1',
    transition: 'color 0.2s ease'
  },
  helperText: {
    fontSize: '12px',
    color: '#6B778C'
  },
  lastChangedText: {
    fontSize: '11px',
    color: '#8993A4',
    marginTop: '6px',
    fontStyle: 'normal',
    fontWeight: '400',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap'
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' }
  },
  deleteButton: {
    padding: '4px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6B778C',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    lineHeight: '1',
    width: '24px',
    height: '24px',
    flexShrink: 0
  },
  deleteButtonInRow: {
    padding: '4px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6B778C',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    lineHeight: '1',
    width: '24px',
    height: '24px',
    marginLeft: 'auto'
  },
  deleteConfirmationPopover: {
    position: 'fixed',
    backgroundColor: 'white',
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.1)',
    zIndex: 10000,
    minWidth: '280px',
    maxWidth: '320px'
  },
  deleteConfirmationContent: {
    padding: '16px'
  },
  deleteConfirmationText: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#172B4D'
  },
  deleteConfirmationRepoName: {
    margin: '0 0 16px 0',
    fontWeight: 600,
    fontSize: '14px',
    color: '#172B4D',
    padding: '8px 12px',
    backgroundColor: '#F4F5F7',
    borderRadius: '3px',
    display: 'inline-block'
  },
  deleteConfirmationButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px'
  },
  deleteCancelButton: {
    padding: '6px 12px',
    backgroundColor: 'white',
    color: '#42526E',
    border: '1px solid #DFE1E6',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease'
  },
  deleteConfirmButton: {
    padding: '6px 12px',
    backgroundColor: '#DE350B',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    opacity: 1
  },
  deleteConfirmButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  tableLoadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
    borderRadius: '8px',
    minHeight: '200px'
  },
  loadingSpinner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },
  searchInput: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '2px solid #DFE1E6',
    borderRadius: '4px',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    boxSizing: 'border-box',
    backgroundColor: 'white',
    color: '#172B4D'
  }
};

// Add CSS keyframes for spinner animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .readonly-projects-select option:hover,
  .readonly-projects-select option:focus,
  .readonly-projects-select option:active {
    background-color: transparent !important;
    color: inherit !important;
  }
`;
if (!document.getElementById('config-styles')) {
  styleSheet.id = 'config-styles';
  document.head.appendChild(styleSheet);
}

export default Config;
